import { useState } from 'react';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    AppBar,
    Box,
    Button,
    Collapse,
    Drawer,
    GlobalStyles,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    type Theme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { setLanguage, type Language } from '@/i18n';
import { SnackbarProvider } from './snackbar/SnackbarProvider';
import logo from './assets/airportliner_logo.svg';

const DRAWER_WIDTH = 224;
/** Collapsed desktop sidebar (prototype): the 24 px icon column with a 28 px inset on both sides. */
const RAIL_WIDTH = 80;
const COLLAPSED_KEY = 'dispo.sidebarCollapsed';

const NAV = [
    { to: '/forecast', key: 'nav.forecast', Icon: EventNoteIcon },
    { to: '/vehicles', key: 'nav.vehicles', Icon: DirectionsCarIcon },
    { to: '/settings', key: 'nav.settings', Icon: SettingsIcon },
] as const;

/** Resting sidebar controls (prototype): 6.6:1 on airport blue; the current page and hover go full white. */
const WHITE_70 = 'rgba(255,255,255,0.7)';
const WHITE_08 = 'rgba(255,255,255,0.08)';
const WHITE_12 = 'rgba(255,255,255,0.12)';
/** Language toggle outline: 4.1:1 on airport blue (WCAG 1.4.11 asks 3:1), MUI's outlined 50 %. */
const WHITE_50 = 'rgba(255,255,255,0.5)';
const focusRing = {
    outline: (theme: Theme) => `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: -2,
};

/** Toggle and logout share the nav item's look: 36 px high, 8 px radius, muted at rest. */
const buttonOnBlueSx = {
    px: 1,
    py: 0.75,
    borderRadius: 1,
    color: WHITE_70,
    '&:hover': { bgcolor: WHITE_08, color: 'common.white' },
    '&.Mui-focusVisible': focusRing,
};

/** Frame-wide: with "reduce motion" on, MUI's slides, fades and collapses finish instantly. */
const reducedMotion = (
    <GlobalStyles
        styles={{
            '@media (prefers-reduced-motion: reduce)': {
                '*, *::before, *::after': {
                    animationDuration: '0.01ms !important',
                    animationIterationCount: '1 !important',
                    transitionDuration: '0.01ms !important',
                },
            },
        }}
    />
);

/** MUI's drawer timing (sharp, 225 ms open / 195 ms close) for the rail width, label fades and footer fold. */
const drawerTiming = (theme: Theme, collapsed: boolean) => ({
    easing: theme.transitions.easing.sharp,
    duration: collapsed ? theme.transitions.duration.leavingScreen : theme.transitions.duration.enteringScreen,
});

/** `collapsed` shrinks it to the icon rail; `onToggle` (desktop only) adds the toggle in the icon column. */
function Sidebar({
    collapsed = false,
    onToggle,
    onNavigate,
}: {
    collapsed?: boolean;
    onToggle?: () => void;
    onNavigate: () => void;
}) {
    const { t, i18n } = useTranslation();
    const { user, logout } = useAuth();
    const theme = useTheme();
    const timing = drawerTiming(theme, collapsed);
    const name = [user?.firstName, user?.lastName].join(' ');
    // Labels stay in the DOM (they name the nav links) and fade out; the narrow paper clips them.
    const label = { opacity: collapsed ? 0 : 1, transition: theme.transitions.create('opacity', timing) };
    const railTooltip = {
        placement: 'right',
        disableHoverListener: !collapsed,
        disableFocusListener: !collapsed,
        disableTouchListener: !collapsed,
    } as const;

    return (
        // nowrap: while the paper animates, labels clip at its edge instead of wrapping and changing heights.
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', whiteSpace: 'nowrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2.5, height: 68 }}>
                {onToggle && (
                    <Tooltip title={t(collapsed ? 'nav.expand' : 'nav.collapse')} placement="right">
                        <IconButton
                            color="inherit"
                            aria-label={t('nav.sidebar')}
                            aria-expanded={!collapsed}
                            onClick={onToggle}
                            sx={buttonOnBlueSx}
                        >
                            {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
                        </IconButton>
                    </Tooltip>
                )}
                <Box sx={label}>
                    <img src={logo} alt="airportLiner" height={28} style={{ display: 'block' }} />
                    <Typography variant="caption" sx={{ display: 'block', letterSpacing: '2px', opacity: 0.8 }}>
                        {t('app.title')}
                    </Typography>
                </Box>
            </Box>
            <List component="nav" sx={{ p: 1.5 }}>
                {NAV.map(({ to, key, Icon }) => (
                    <Tooltip key={to} title={t(key)} {...railTooltip}>
                        <ListItemButton
                            component={NavLink}
                            to={to}
                            onClick={onNavigate}
                            sx={{
                                borderRadius: 1,
                                mb: 0.25,
                                px: 2,
                                py: 0.75,
                                color: WHITE_70,
                                '&:hover': { bgcolor: WHITE_08, color: 'common.white' },
                                '&.active, &.active:hover': { bgcolor: WHITE_12, color: 'common.white' },
                                '&.Mui-focusVisible': focusRing,
                            }}
                        >
                            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                <Icon />
                            </ListItemIcon>
                            <ListItemText primary={t(key)} sx={{ ...label, my: 0 }} />
                        </ListItemButton>
                    </Tooltip>
                ))}
            </List>
            <Box sx={{ mt: 'auto', px: 2.5, py: 1.5, borderTop: `1px solid ${WHITE_12}` }}>
                {/* The rail drops name and language (set once, expand to change); the divider glides along. */}
                <Collapse in={!collapsed} easing={timing.easing} timeout={timing.duration}>
                    <Box sx={{ display: 'grid', gap: 1, pb: 1 }}>
                        <Typography variant="body2" noWrap title={name}>
                            {name}
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={i18n.language}
                            aria-label={t('language.label')}
                            onChange={(_, value: Language | null) => value && setLanguage(value)}
                            sx={{
                                // MUI's hover and selected tints are black-on-light; on the blue sidebar they vanish.
                                '& .MuiToggleButton-root': {
                                    color: 'inherit',
                                    borderColor: WHITE_50,
                                    px: 1.5,
                                    '&:hover': { bgcolor: WHITE_08 },
                                    '&.Mui-focusVisible': focusRing,
                                },
                                '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                                    color: 'primary.main',
                                    bgcolor: 'common.white',
                                    borderColor: 'common.white',
                                },
                            }}
                        >
                            <ToggleButton value="de">DE</ToggleButton>
                            <ToggleButton value="en">EN</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Collapse>
                {/* One element in both states: 24 px glyph in the icon column, label at 64 px, like a nav item. */}
                <Tooltip title={t('auth.logoutAs', { name })} {...railTooltip}>
                    <Button
                        color="inherit"
                        startIcon={<LogoutIcon />}
                        onClick={logout}
                        sx={{
                            ...buttonOnBlueSx,
                            px: 2,
                            // The nav item's box: bleeds 8 px and tracks the paper width while it animates (a
                            // <button> never stretches on its own, so the width is explicit).
                            display: 'flex',
                            justifyContent: 'flex-start',
                            mx: -1,
                            width: 'calc(100% + 16px)',
                            minWidth: 0,
                            overflow: 'hidden',
                            '& .MuiButton-startIcon': { ml: 0, mr: 1.5 },
                            '& .MuiButton-startIcon svg': { fontSize: '1.5rem' },
                        }}
                    >
                        <Box component="span" sx={label}>
                            {t('auth.logout')}
                        </Box>
                    </Button>
                </Tooltip>
            </Box>
        </Box>
    );
}

/**
 * Sidebar-only frame (prototype layout): permanent from `md` up, collapsible to an icon rail (state in
 * localStorage); hamburger + temporary drawer below `md`.
 */
export function AppShell() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [open, setOpen] = useState(false);
    // Anything but the exact string we write (missing, garbage) means expanded.
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true');
    const toggleCollapsed = () => {
        localStorage.setItem(COLLAPSED_KEY, String(!collapsed));
        setCollapsed(!collapsed);
    };
    const paperSx = {
        width: DRAWER_WIDTH,
        boxSizing: 'border-box',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        border: 0,
    };
    const railSx = {
        width: collapsed ? RAIL_WIDTH : DRAWER_WIDTH,
        transition: theme.transitions.create('width', drawerTiming(theme, collapsed)),
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {reducedMotion}
            {isDesktop ? (
                <Drawer
                    variant="permanent"
                    sx={{
                        ...railSx,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': { ...paperSx, ...railSx, overflowX: 'hidden' },
                    }}
                >
                    <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} onNavigate={() => undefined} />
                </Drawer>
            ) : (
                <>
                    <AppBar position="fixed" elevation={0}>
                        <Toolbar variant="dense">
                            <IconButton
                                color="inherit"
                                edge="start"
                                aria-label={t('nav.menu')}
                                onClick={() => setOpen(true)}
                                sx={{ mr: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <img src={logo} alt="airportLiner" height={24} />
                        </Toolbar>
                    </AppBar>
                    <Drawer
                        variant="temporary"
                        open={open}
                        onClose={() => setOpen(false)}
                        ModalProps={{ keepMounted: true }}
                        sx={{ '& .MuiDrawer-paper': paperSx }}
                    >
                        <Sidebar onNavigate={() => setOpen(false)} />
                    </Drawer>
                </>
            )}
            <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
                {!isDesktop && <Toolbar variant="dense" />}
                <SnackbarProvider>
                    <Outlet />
                </SnackbarProvider>
            </Box>
        </Box>
    );
}
